import React, { useState, useEffect } from 'react';

const FreeDiscussion = ({ user, onShowAuth, Icons, apiBase }) => {
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Fetch Approved Comments ---
    const fetchComments = async () => {
        setIsLoading(true);
        try {
            // دریافت نظرات تایید شده (approved=1)
            const response = await fetch(`${apiBase}/comments.php?approved=1`);
            const data = await response.json();
            
            if (Array.isArray(data)) {
                setComments(data);
            }
        } catch (error) {
            console.error("خطا در دریافت نظرات:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (apiBase) fetchComments();
    }, [apiBase]);

    // --- Handle Submit ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!user) {
            onShowAuth();
            return;
        }

        if (!commentText.trim()) return;

        setIsSubmitting(true);
        
        // دریافت نام کاربری
        const userName = user.full_name || user.email?.split('@')[0] || 'کاربر ناشناس';

        try {
            const response = await fetch(`${apiBase}/comments.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user.id || 0,
                    name: userName,
                    text: commentText.trim()
                })
            });

            const result = await response.json();

            if (result.success) {
                setCommentText('');
                alert('پیام شما با موفقیت ثبت شد و پس از تایید مدیریت نمایش داده می‌شود.');
            } else {
                throw new Error('خطا در ثبت پیام');
            }
        } catch (error) {
            alert('خطا در ارسال پیام. لطفا دوباره تلاش کنید.');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8 fade-in">
            <div className="max-w-4xl mx-auto">
                
                {/* Header Section */}
                <div className="text-center mb-10">
                    <h2 className="text-3xl font-black text-slate-800 mb-3">تالار گفت‌و‌گو</h2>
                    <p className="text-slate-500 text-sm">
                        فضایی برای تبادل نظر و پرسش و پاسخ دانشجویان
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    {/* --- Sidebar: Form --- */}
                    <div className="lg:col-span-1">
                        <div className="glass-card p-6 sticky top-24">
                            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                                <Icons.Edit size={20} className="text-blue-600" />
                                ارسال پیام جدید
                            </h3>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <textarea 
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        placeholder={user ? "سوال یا نظر خود را بنویسید..." : "برای ارسال پیام ابتدا وارد شوید"}
                                        disabled={!user || isSubmitting}
                                        rows="5"
                                        className="w-full bg-[#f0f0f3] shadow-neu-inset rounded-2xl p-4 outline-none text-slate-700 text-sm resize-none transition-all focus:ring-2 focus:ring-blue-200"
                                    ></textarea>
                                </div>

                                {user ? (
                                    <button 
                                        type="submit" 
                                        disabled={isSubmitting || !commentText.trim()}
                                        className="w-full bg-[#f0f0f3] shadow-neu hover:shadow-neu-inset text-blue-600 font-bold py-3 rounded-xl transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? 'در حال ارسال...' : (
                                            <>
                                                <span>ارسال پیام</span>
                                                <Icons.Check size={18} />
                                            </>
                                        )}
                                    </button>
                                ) : (
                                    <button 
                                        type="button"
                                        onClick={onShowAuth}
                                        className="w-full bg-[#f0f0f3] shadow-neu hover:shadow-neu-inset text-slate-500 font-bold py-3 rounded-xl transition-all active:scale-95 text-sm"
                                    >
                                        ورود به حساب کاربری
                                    </button>
                                )}
                                
                                <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                                    پیام‌های شما پس از بررسی و تایید توسط ادمین در سایت نمایش داده می‌شوند.
                                </p>
                            </form>
                        </div>
                    </div>

                    {/* --- Main Content: Comments List --- */}
                    <div className="lg:col-span-2">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-bold text-slate-700 flex items-center gap-2">
                                <Icons.List size={20} className="text-blue-600" />
                                آخرین نظرات ({comments.length})
                            </h3>
                            <button 
                                onClick={fetchComments} 
                                className="text-slate-400 hover:text-blue-600 transition-colors p-2"
                                title="بروزرسانی"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
                            </button>
                        </div>

                        <div className="space-y-5">
                            {isLoading ? (
                                <div className="text-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-300 border-t-blue-500"></div>
                                    <p className="mt-4 text-slate-400 text-sm">در حال بارگذاری...</p>
                                </div>
                            ) : comments.length === 0 ? (
                                <div className="glass-card p-10 text-center">
                                    <Icons.User size={48} className="mx-auto text-slate-300 mb-4" />
                                    <p className="text-slate-500 font-medium">هنوز پیامی ثبت نشده است.</p>
                                    <p className="text-slate-400 text-sm mt-2">اولین نفر باشید که نظر می‌دهد!</p>
                                </div>
                            ) : (
                                comments.map((comment) => (
                                    <div key={comment.id} className="glass-card p-5 group hover:translate-y-[-2px] transition-transform">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-10 h-10 rounded-full bg-[#f0f0f3] shadow-neu flex items-center justify-center text-blue-500">
                                                    <Icons.User size={20} />
                                                </div>
                                            </div>
                                            <div className="flex-grow min-w-0">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h5 className="font-bold text-slate-700 text-sm truncate">
                                                        {comment.name}
                                                    </h5>
                                                    <span className="text-[10px] text-slate-400 bg-[#f0f0f3] shadow-neu-inset px-2 py-1 rounded-lg">
                                                        {new Date(comment.created_at).toLocaleDateString('fa-IR')}
                                                    </span>
                                                </div>
                                                <p className="text-slate-600 text-sm leading-7 text-justify whitespace-pre-wrap">
                                                    {comment.text}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default FreeDiscussion;